import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { serverNow } from '../lib/clock'
import { eachHourOfInterval, startOfHour, format } from 'date-fns'
import { RefreshCw, Clock, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import { ScopeChip } from './MineScopeSelect'
import { equipmentOf } from '../equipment'
import { LoadingState, ErrorState, EmptyState } from './ViewState'
import { cycleStateLabel } from '../i18n/status'

// Os estados são os baldes de paragem crus vindos da API (stops.bucket), mais
// working. Um balde que não esteja aqui cai na cor do standby e o tooltip imprime
// o token cru ("non_productive") ao operador: a lista tem de acompanhar o backend.
const STATE_COLOR = {
  working: '#17b26a',
  delay: '#f79009',
  standby: '#94a3b8',
  non_productive: '#dd2590',
  downtime: '#f04438',
}

const STATE_KEYS = {
  working: 'production:timeline.state_working',
  delay: 'production:timeline.state_delay',
  standby: 'production:timeline.state_standby',
  non_productive: 'production:timeline.state_non_productive',
  downtime: 'production:timeline.state_downtime',
}

const WINDOWS = [
  { label: '24h', hours: 24 },
  { label: '12h', hours: 12 },
  { label: '8h', hours: 8 },
]

const LABEL_W = 220
// O /api/timeline pagina por equipamento (limite do servidor: 200 por página).
const PAGE_SIZE = 25

function fmtDur(secs) {
  secs = Math.max(0, Math.round(Number(secs) || 0))
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const ms = (v, fallback) => {
  const n = v ? new Date(v).getTime() : NaN
  return Number.isFinite(n) ? n : fallback
}

// O backend já entrega o segmento recortado à janela (visibleStart/visibleEnd/seconds) e
// a duração inteira em totalSeconds. A barra usa o recorte; o tooltip usa a duração
// inteira, senão uma paragem que vem de ontem aparecia com o tamanho da janela como se
// fosse a sua duração real.
function segSpan(seg, fromMs, toMs) {
  const start = ms(seg.start, fromMs)
  const end = ms(seg.end, toMs)
  const visStart = Math.max(ms(seg.visibleStart, start), fromMs)
  const visEnd = Math.min(ms(seg.visibleEnd, end), toMs)
  const visibleSecs = seg.seconds != null
    ? Math.max(0, Number(seg.seconds) || 0)
    : Math.max(0, (visEnd - visStart) / 1000)
  const totalSecs = seg.totalSeconds != null
    ? Math.max(0, Number(seg.totalSeconds) || 0)
    : Math.max(0, (end - start) / 1000)
  return { start, end, visStart, visEnd, visibleSecs, totalSecs }
}

function fmtClockRange(start, end) {
  const t1 = start ? format(new Date(start), 'HH:mm') : '?'
  const t2 = end ? format(new Date(end), 'HH:mm') : '?'
  return `${t1}–${t2}`
}

function Legend() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-4 flex-wrap">
      {Object.keys(STATE_KEYS).map(k => (
        <span key={k} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: STATE_COLOR[k] }} />
          {t(STATE_KEYS[k])}
        </span>
      ))}
    </div>
  )
}

function TimeRuler({ ticks }) {
  return (
    <div className="flex items-end">
      <div className="shrink-0" style={{ width: LABEL_W }} />
      <div className="relative flex-1 h-5">
        {ticks.map((t, i) => (
          <div key={i} className="absolute top-0 bottom-0 flex flex-col items-start"
            style={{ left: `${t.pct}%` }}>
            <span className="text-[10px] text-slate-400 dark:text-slate-500 tabular-nums -translate-x-1/2 whitespace-nowrap">{t.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function TrackRow({ row, eq, fromMs, toMs, windowSecs, ticks }) {
  const { t } = useTranslation()
  const segments = Array.isArray(row.segments) ? row.segments : []
  return (
    <div className="flex items-center gap-0 py-1">
      <div className="shrink-0 flex items-center gap-2 pr-3" style={{ width: LABEL_W }}>
        <span className="equip-icon block w-5 h-5 text-slate-500 dark:text-slate-400 shrink-0"
          style={{ WebkitMaskImage: `url(/equipments/${eq.svg}.svg)`, maskImage: `url(/equipments/${eq.svg}.svg)` }} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate leading-tight">{eq.name || row.device}</p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate leading-tight">{row.typeName || eq.label || ''}</p>
        </div>
      </div>

      <div className="relative h-7 rounded bg-slate-100 dark:bg-slate-800 overflow-hidden flex-1">
        {ticks.map((t, i) => (
          <div key={i} className="absolute top-0 bottom-0 w-px bg-slate-200/70" style={{ left: `${t.pct}%` }} />
        ))}
        {segments.map((seg, i) => {
          const { start, end, visStart, visEnd, visibleSecs, totalSecs } = segSpan(seg, fromMs, toMs)
          if (visEnd <= fromMs || visStart >= toMs) return null
          const left = ((visStart - fromMs) / (toMs - fromMs)) * 100
          const width = Math.max(0.5, (visibleSecs / windowSecs) * 100)
          const color = STATE_COLOR[seg.state] || STATE_COLOR.standby
          const label = cycleStateLabel(t, seg.label) || (STATE_KEYS[seg.state] ? t(STATE_KEYS[seg.state]) : seg.state || '')
          const clipped = start < fromMs || end > toMs
          const title = `${label} · ${fmtDur(totalSecs)} · ${fmtClockRange(seg.start, seg.end)}` +
            (clipped ? ` · ${t('production:timeline.clipped', { visible: fmtDur(visibleSecs) })}` : '')
          return (
            <div key={i}
              className={`absolute top-0 bottom-0 rounded-[2px] ${start < fromMs ? 'border-l-2 border-dashed border-white/70' : ''}${end > toMs ? ' border-r-2 border-dashed border-white/70' : ''}`}
              style={{ left: `${Math.max(0, left)}%`, width: `${width}%`, background: color }}
              title={title} />
          )
        })}
      </div>
    </div>
  )
}

export default function TimelineView() {
  const { t } = useTranslation()
  const { devices, categories, mines } = useWS()
  const { mineQS } = useScope()
  const [hours, setHours] = useState(24)
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const abortRef = useRef(null)
  const load = useCallback((h, off) => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setLoading(true)
    const to = new Date()
    const from = new Date(to.getTime() - h * 3600 * 1000)
    const win = { from: from.toISOString(), to: to.toISOString() }
    const qs = `from=${encodeURIComponent(win.from)}&to=${encodeURIComponent(win.to)}` +
      `&limit=${PAGE_SIZE}&offset=${off}${mineQS ? '&' + mineQS : ''}`
    fetch(`/api/timeline?${qs}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(d => { setData(d && typeof d === 'object' ? d : { window: win, rows: [] }); setError(null) })
      .catch(err => { if (err.name !== 'AbortError') { setError(err.message); setData({ window: win, rows: [] }) } })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
  }, [mineQS])

  // Trocar de janela ou de mina volta à primeira página: a numeração é da lista de
  // equipamentos daquela consulta, então manter o offset mostrava uma página vazia.
  useEffect(() => { setOffset(0) }, [hours, mineQS])

  useEffect(() => {
    load(hours, offset)
    return () => abortRef.current?.abort()
  }, [load, hours, offset])

  const win = data?.window || {}
  // A janela é do servidor (win.from/win.to vêm da resposta); o fallback tem que
  // sair do mesmo relógio, senão os segmentos aparecem deslocados na barra.
  const fromMs = win.from ? new Date(win.from).getTime() : serverNow() - hours * 3600 * 1000
  const toMs = win.to ? new Date(win.to).getTime() : serverNow()
  const windowSecs = Math.max(1, (toMs - fromMs) / 1000)
  const rows = Array.isArray(data?.rows) ? data.rows : []
  const page = data?.page || { offset: 0, limit: PAGE_SIZE, total: rows.length, truncated: false }
  const total = Number(page.total) || rows.length

  const ticks = useMemo(() => {
    if (!(toMs > fromMs)) return []
    const fd = new Date(fromMs)
    const td = new Date(toMs)
    let hourList = []
    try {
      hourList = eachHourOfInterval({ start: startOfHour(fd), end: td })
    } catch {
      return []
    }
    const span = toMs - fromMs
    return hourList
      .map(d => d.getTime())
      .filter(ms => ms >= fromMs && ms <= toMs)
      .map(ms => ({ pct: ((ms - fromMs) / span) * 100, label: format(new Date(ms), 'HH:mm') }))
  }, [fromMs, toMs])

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-3 flex items-center gap-4">
        <div className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
          <Clock size={15} className="text-brand" />
          {t('production:timeline.title')}
        </div>
        <ScopeChip />
        <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
          {WINDOWS.map(w => (
            <button key={w.hours} onClick={() => setHours(w.hours)}
              className={`px-3 py-1 text-xs font-medium transition-colors ${hours === w.hours ? 'bg-brand text-white' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
              {w.label}
            </button>
          ))}
        </div>
        <button onClick={() => load(hours, offset)}
          className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 px-2 py-1 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> {t('common:actions.refresh')}
        </button>

        {/* A tela mostra uma PÁGINA de equipamentos. Sem o contador e as setas, uma frota
            maior que a página parecia a frota inteira. */}
        {total > page.limit && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            <button onClick={() => setOffset(Math.max(0, offset - page.limit))} disabled={offset <= 0}
              className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent">
              <ChevronLeft size={14} />
            </button>
            <span className="tabular-nums whitespace-nowrap">
              {t('production:timeline.page_range', { from: rows.length ? offset + 1 : 0, to: offset + rows.length, total })}
            </span>
            <button onClick={() => setOffset(offset + page.limit)} disabled={offset + rows.length >= total}
              className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent">
              <ChevronRight size={14} />
            </button>
          </div>
        )}
        <div className="ml-auto">
          <Legend />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
        {loading && rows.length === 0 ? (
          <LoadingState label={t('production:timeline.loading')} />
        ) : error && rows.length === 0 ? (
          <ErrorState message={error} onRetry={() => load(hours, offset)} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Clock} message={t('production:timeline.empty')} />
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-4">
            {page.truncated && (
              <p className="mb-2 flex items-start gap-1.5 text-[11px] text-status-stop">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                {t('production:timeline.truncated')}
              </p>
            )}
            <TimeRuler ticks={ticks} />
            <div className="mt-1 divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((row, i) => {
                const dev = devices.find(d => d.name === row.device) || { name: row.device }
                const eq = equipmentOf(dev, categories, mines)
                return (
                  <TrackRow key={row.device || i} row={row} eq={eq}
                    fromMs={fromMs} toMs={toMs} windowSecs={windowSecs} ticks={ticks} />
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
